import { sum } from '../core/util';
import settings from '../settings.json';
import { I_CLASS_MINIMUM } from '../flows/shared/utils';
import { minimumSummitIClassInvestAmount } from '../flows/shared/summit.utils';

const {
  stocks: stockMap,
  bonds: bondMap,
  shortTerm: shortTermMap
} = settings.productTypes;

export const updateAllocationTotals = shoppingCart => {
  let stocks = 0;
  let bonds = 0;
  let shortTerm = 0;

  // only update fund allocations based on investment percentage when allocating money to multiple funds
  if (shoppingCart.length > 1) {
    shoppingCart.forEach(item => {
      const iPercent = Number.isNaN(Number(item.percent))
        ? 0
        : item.percent / 100;
      const iStock = iPercent * (item.allocation.stocks / 100) * 100;
      const iBonds = iPercent * (item.allocation.bonds / 100) * 100;
      const iShortTerm =
        iPercent * (item.allocation.shortTermReserves / 100) * 100;
      stocks += iStock;
      bonds += iBonds;
      shortTerm += iShortTerm;
    });
    return {
      overallStocks: stocks.toFixed(2),
      overallBonds: bonds.toFixed(2),
      overallShortTerm: shortTerm.toFixed(2)
    };
  }
  shoppingCart.forEach(item => {
    const iStock = (item.allocation.stocks / 100) * 100;
    const iBonds = (item.allocation.bonds / 100) * 100;
    const iShortTerm = (item.allocation.shortTermReserves / 100) * 100;
    stocks += iStock;
    bonds += iBonds;
    shortTerm += iShortTerm;
  });
  return {
    overallStocks: stocks.toFixed(2),
    overallBonds: bonds.toFixed(2),
    overallShortTerm: shortTerm.toFixed(2)
  };
};

export const iClassFundLimits = (amount, iClassRewards) =>
  (iClassRewards && parseFloat(amount) >= minimumSummitIClassInvestAmount) ||
  (!iClassRewards && parseFloat(amount) >= I_CLASS_MINIMUM);

export const mapFundLimits = rewardsAvailable => f => ({
  ...f,
  meetsMinimumInvestmentAmount: f.iClass
    ? iClassFundLimits(f.amount, rewardsAvailable.iClassFunds)
    : parseFloat(f.amount) >= f.minimumInvestmentAmount,
  meetsAccountMinimumOpenIRA: f.iClass
    ? iClassFundLimits(f.amount, rewardsAvailable.iClassFunds)
    : parseFloat(f.amount) >= f.accountMinimumOpenIRA
});

const filterSuppressedClosedFund = rewardsAvailable => fund => {
  if (rewardsAvailable.closedFunds) {
    /* 
      In the case a Summit Rewards member has chosen a suppressed closed fund
      regardless of reward `openToSubsequentInvestments` must be true for all closed funds
      before the customer can buy in
    */
    return (
      (!fund.openToNewInvestors && fund.openToSubsequentInvestments) ||
      fund.openToNewInvestors
    );
  }

  /* default to return the fund by forcing a truthy filter outcome */
  return true;
};

export const roundFundAllocations = fund => {
  const assetAllocation = fund?.allocation;
  return {
    ...fund,
    allocation: {
      stocks: Math.round(assetAllocation?.stocks),
      bonds: Math.round(assetAllocation?.bonds),
      shortTermReserves: Math.round(assetAllocation?.shortTermReserves),
      asOfDate: assetAllocation?.asOfDate,
      fundAllocationsPresent: assetAllocation?.fundAllocationsPresent
    }
  };
};

export const mapFundAllocation = fund => {
  const rankings = fund.assetAllocationGroupedRankings || [];
  const bucketRankings = typeMap => {
    const filteredRankings = rankings.filter(ranking =>
      typeMap.includes(ranking.name)
    );

    const mappedRankings = filteredRankings.map(
      ranking => ranking.percentTotalNetAssets
    );

    return sum(mappedRankings);
  };

  const stocks = bucketRankings(stockMap);
  const bonds = bucketRankings(bondMap);
  const shortTermReserves = bucketRankings(shortTermMap);
  const { asOfDate } = rankings.find(ranking => ranking.asOfDate) || {};
  const fundAllocationsPresent = stocks + bonds + shortTermReserves !== 0;

  return {
    ...fund,
    allocation: {
      stocks,
      bonds,
      shortTermReserves,
      asOfDate,
      fundAllocationsPresent
    }
  };
};

export const mapFundLaterFundData = (selectedFunds, mutualFundsData) =>
  selectedFunds.map(selectedFund => ({
    ...selectedFund,
    ...mutualFundsData.find(fund => fund.ticker === selectedFund.ticker)
  }));

export default ({
  selectedFunds,
  mutualFundsWithAllocations,
  rewardsAvailable = { closedFunds: false, iClassFunds: false },
  totalAmount
}) => {
  const fundsWithCategories = selectedFunds
    .map(selectedFund => ({
      ...selectedFund,
      ...mutualFundsWithAllocations.find(
        fund => fund.ticker === selectedFund.ticker
      )
    }))
    .filter(filterSuppressedClosedFund(rewardsAvailable))
    .map(mapFundAllocation)
    .map(roundFundAllocations)
    .map(mapFundLimits(rewardsAvailable));

  const validCart =
    fundsWithCategories.length &&
    fundsWithCategories.every(f => f.meetsAccountMinimumOpenIRA) &&
    fundsWithCategories
      .map(fund => fund.amount)
      .reduce((prev, next) => prev + next, 0)
      .toFixed(2) === parseFloat(totalAmount).toFixed(2);

  const validTaxableCart =
    fundsWithCategories.length &&
    fundsWithCategories.every(f => f.meetsMinimumInvestmentAmount) &&
    fundsWithCategories
      .map(fund => fund.amount)
      .reduce((prev, next) => prev + next, 0) === totalAmount;

  const totalAllocationsValid =
    fundsWithCategories.length &&
    fundsWithCategories.every(
      f => f.allocation.fundAllocationsPresent === true
    );

  const {
    overallBonds,
    overallStocks,
    overallShortTerm
  } = updateAllocationTotals(fundsWithCategories);

  return {
    validCart,
    validTaxableCart,
    totalAllocationsValid,
    fundsWithCategories,
    categoryTotals: { overallBonds, overallStocks, overallShortTerm }
  };
};
